<!-- src/views/Messages/Compose.vue -->
<template>
  <div class="page">
    <div
      v-if="role === 'employee' && !isReplyMode"
      class="warn"
    >
      You can only reply to messages sent to you.
    </div>

    <div class="head">
      <h2 class="title">New Message</h2>
      <router-link class="btn secondary" :to="{ name: 'messages-inbox' }">← Back</router-link>
    </div>

    <div v-if="!hasBackend" class="warn">
      Backend auth not connected (no token). Once your real login is wired, Compose will send messages.
    </div>

    <div v-if="error" class="err">{{ error }}</div>

    <div class="card">
      <!-- Branch selector (only super admin) -->
      <div class="row" v-if="isSuperAdmin">
        <label>Branch</label>
        <select class="input" v-model="branchId" @change="onBranchChange">
          <option value="">All Branches</option>
          <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }} ({{ b.id }})</option>
        </select>
        <div class="hint">Tip: Pick a branch, then pick a user. Deep-links auto-fill this.</div>
      </div>

      <!-- To user selector -->
      <div class="row">
        <label>To</label>

        <!-- ✅ Read-only display when user can't start threads OR in reply mode -->
        <div v-if="isReplyMode || !canStartNewThread" class="rowInline">
          <input
            class="input"
            :value="recipientLabel"
            disabled
          />
        </div>

        <!-- ✅ Normal dropdown only for sender roles in new-thread mode -->
        <div v-else class="rowInline">
          <select
            class="input"
            v-model="toUserId"
          >
            <option value="">Select user…</option>
            <option
              v-for="u in filteredUsers"
              :key="u.id"
              :value="String(u.id)"
            >
              {{ u.name }} • {{ u.role }} • {{ u.branchId }} ({{ u.id }})
            </option>
          </select>
        </div>

        <div class="hint" v-if="!canStartNewThread && !isReplyMode">
          You can’t start a new message. Use Reply from Inbox.
        </div>

        <div class="hint" v-else-if="canStartNewThread && !isReplyMode && !filteredUsers.length">
          No users found for this scope.
        </div>
      </div>

      <div class="row2">
        <div class="row">
          <label>Priority</label>
          <select class="input" v-model="priority">
            <option>LOW</option>
            <option>NORMAL</option>
            <option>HIGH</option>
          </select>
        </div>
      </div>

      <div class="row2">
        <div class="row">
          <label>Scope</label>
          <select class="input" v-model="scopeType" :disabled="isReplyMode">
            <option>NONE</option>
            <option>LEAD</option>
            <option>QUOTATION</option>
            <option>ORDER</option>
          </select>
          <div class="hint">Use scope to attach the message to a lead/order. Deep-links fill this automatically.</div>
        </div>

        <div class="row">
          <label>Scope ID</label>
          <input
            class="input"
            v-model="scopeId"
            :disabled="isReplyMode"
            placeholder="optional (number)"
          />
        </div>
      </div>

      <div class="row">
        <label>Subject</label>
        <input class="input" v-model="subject" placeholder="Short summary (optional)" />
      </div>

      <div class="row">
        <label>Message</label>
        <textarea class="input area" v-model="body" placeholder="Write the note/task..." />
      </div>

      <div class="actions">
        <button
          class="btn"
          :disabled="sendDisabled"
          @click="send"
        >
          {{ sending ? 'Sending…' : 'Send' }}
        </button>

        <button
          v-if="!isReplyMode"
          class="btn secondary"
          :disabled="sending"
          @click="clear"
        >
          Clear
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessagesCompose',
  data () {
    return {
      branches: [],
      users: [],

      // selection
      branchId: '',
      toUserId: '',

      // message fields
      priority: 'NORMAL',
      scopeType: 'NONE',
      scopeId: '',
      subject: '',
      body: '',

      sending: false,
      error: null,

      // reply mode
      inReplyToId: null,

      // reply defaults (so we can show To label even without users list)
      replyToName: '',
      replyToUserId: ''
    }
  },
  computed: {
    hasBackend () {
      try { return !!localStorage.getItem('edda_token') } catch (e) { return false }
    },
    currentUser () {
      return this.$store.getters['auth/currentUser'] || {}
    },
    role () {
      return (this.currentUser && this.currentUser.role) ? this.currentUser.role : ''
    },
    myBranchId () {
      return (this.currentUser && this.currentUser.branchId) ? this.currentUser.branchId : ''
    },

    isSuperAdmin () { return this.role === 'super_admin' },
    isBranchAdmin () { return this.role === 'branch_admin' },
    isManager () { return this.role === 'branch_manager' },

    canStartNewThread () {
      return this.isSuperAdmin || this.isBranchAdmin || this.isManager
    },

    isReplyMode () { return !!this.inReplyToId },

    // ✅ show the recipient even when dropdown is disabled / empty
    recipientLabel () {
      const id = String(this.toUserId || this.replyToUserId || '').trim()
      if (!id) return '—'

      const list = Array.isArray(this.users) ? this.users : []
      const u = list.find(x => String(x.id) === id)
      if (u && u.name) return u.name + ' (' + id + ')'

      if (this.replyToName) return this.replyToName + ' (' + id + ')'

      return 'User #' + id
    },

    sendDisabled () {
      if (this.sending) return true
      if (!this.hasBackend) return true
      if (!this.body || !String(this.body).trim()) return true

      // Non-senders must be in reply mode
      if (!this.canStartNewThread && !this.isReplyMode) return true

      // Senders need a selected recipient for new thread
      if (this.canStartNewThread && !this.isReplyMode && !this.toUserId) return true

      // Reply mode should always have recipient populated from backend defaults
      if (this.isReplyMode && !this.toUserId) return true

      return false
    },

    // Who can message whom (for sender roles only; reply defaults come from backend)
    allowedUsers () {
      const all = Array.isArray(this.users) ? this.users : []

      if (this.isSuperAdmin) return all

      if (this.isBranchAdmin) {
        return all.filter(u =>
          u.role === 'super_admin' ||
          String(u.branchId) === String(this.myBranchId)
        )
      }

      if (this.isManager) {
        return all.filter(u =>
          u.role === 'super_admin' ||
          String(u.branchId) === String(this.myBranchId) ||
          ['rm', 'purchase', 'production', 'qc', 'dispatch', 'installer', 'finance'].includes(u.role)
        )
      }

      return []
    },

    filteredUsers () {
      const list = this.allowedUsers.slice(0)

      if (this.isSuperAdmin && this.branchId) {
        return list.filter(u => String(u.branchId) === String(this.branchId))
      }

      return list
    }
  },
  created () {
    this.bootstrapFromQuery()
    this.loadLookups()
    this.loadReplyDefaults()
  },
  watch: {
    '$route.query': {
      deep: true,
      handler () {
        this.bootstrapFromQuery()
        this.loadLookups()
        this.loadReplyDefaults()
      }
    }
  },
  methods: {
    bootstrapFromQuery () {
      const q = (this.$route && this.$route.query) ? this.$route.query : {}

      // reply mode
      if (q.replyTo) this.inReplyToId = String(q.replyTo)
      else this.inReplyToId = null

      // branch filter only for super admin
      if (q.branchId && this.isSuperAdmin) this.branchId = String(q.branchId)

      // deep-link recipient for "new message"
      // (still allow setting it even if users list not loaded yet)
      if (!this.inReplyToId && q.to) {
        this.toUserId = String(q.to)
      }

      // scope
      if (q.scopeType) this.scopeType = this.normalizeScopeType(q.scopeType || 'NONE')
      if (q.scopeId !== undefined && q.scopeId !== null && q.scopeId !== '') this.scopeId = String(q.scopeId)

      // subject/body (Inbox sets these for reply)
      if (q.subject) this.subject = String(q.subject)
      if (q.body) this.body = String(q.body)
    },

    normalizeScopeType (t) {
      const s = String(t || '').toUpperCase()
      if (s === 'LEAD' || s === 'ORDER' || s === 'QUOTATION' || s === 'NONE') return s

      const low = String(t || '').toLowerCase()
      if (low === 'order') return 'ORDER'
      if (low === 'quote' || low === 'quotation') return 'QUOTATION'
      if (low === 'lead' || low === 'inquiry' || low === 'followup' || low === 'docs') return 'LEAD'
      return 'NONE'
    },

    async loadReplyDefaults () {
      if (!this.hasBackend) return
      if (!this.inReplyToId) return

      try {
        const api = this.$api
        const res = await api.get(`/messages/${this.inReplyToId}/reply_defaults`)
        const d = res && res.data ? res.data : {}

        // backend should return: toUserId, scopeType, scopeId, subject (optional)
        if (d.toUserId) {
          this.toUserId = String(d.toUserId)
          this.replyToUserId = String(d.toUserId)
        }
        if (d.toName) this.replyToName = String(d.toName)

        if (d.scopeType) this.scopeType = String(d.scopeType).toUpperCase()
        if (d.scopeId !== undefined && d.scopeId !== null) this.scopeId = String(d.scopeId)

        if (!this.subject && d.subject) this.subject = String(d.subject)
      } catch (e) {
        console.error('loadReplyDefaults failed', e)
        this.error = 'Could not load reply details.'
      }
    },

    async loadLookups () {
      if (!this.hasBackend) return

      try {
        const api = this.$api

        if (this.isSuperAdmin) {
          const br = await api.get('/branches')
          this.branches = Array.isArray(br.data) ? br.data : []
        } else {
          this.branchId = this.myBranchId || ''
        }

        // Only sender roles need recipients list.
        // BUT: if we already have a toUserId from deep-link, we still want names when possible.
        // We'll load recipients for senders only to keep permissions consistent.
        if (this.canStartNewThread) {
          const us = await api.get('/messages/recipients')
          this.users = Array.isArray(us.data) ? us.data : []
        } else {
          this.users = []
        }

        // Infer branchId for super admin from selected recipient (compose mode only)
        if (this.isSuperAdmin && !this.branchId && this.toUserId && !this.isReplyMode) {
          const to = (this.users || []).find(u => String(u.id) === String(this.toUserId))
          if (to && to.branchId) this.branchId = String(to.branchId)
        }

        // ✅ Do NOT auto-clear toUserId. Deep-links should “stick”.
        // If the recipient is filtered out, user can change branch filter or recipient manually.
      } catch (e) {
        console.error('Compose loadLookups failed', e)
      }
    },

    onBranchChange () {
      if (!this.isSuperAdmin) return
      if (this.isReplyMode) return
      // keep toUserId; let UI show it, and sender can adjust if needed
    },

    clear () {
      if (this.isReplyMode) return

      this.toUserId = ''
      this.priority = 'NORMAL'
      this.scopeType = 'NONE'
      this.scopeId = ''
      this.subject = ''
      this.body = ''
      this.error = null
    },

    async send () {
      this.error = null

      const body = (this.body || '').trim()
      if (!body) {
        this.error = 'Message is required.'
        return
      }

      // Non-senders: must be replying
      if (!this.canStartNewThread && !this.isReplyMode) {
        this.error = 'You can only reply from an existing message.'
        return
      }

      // Senders: must choose recipient for new thread
      if (this.canStartNewThread && !this.isReplyMode && !this.toUserId) {
        this.error = 'Recipient is required.'
        return
      }

      const payload = {
        toUserId: String(this.toUserId || '').trim() || null,
        inReplyToId: this.inReplyToId ? Number(this.inReplyToId) : null,
        priority: this.priority,
        scopeType: this.scopeType,
        scopeId: this.scopeType === 'NONE' ? null : (Number(this.scopeId || 0) || null),
        subject: (this.subject || '').trim() || null,
        body
      }

      this.sending = true
      try {
        await this.$store.dispatch('messages/sendMessage', payload)
        this.$router.push({ name: 'messages-inbox' })
      } catch (e) {
        this.error = (e && e.message) || 'Failed to send'
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style scoped>
.page { max-width: 900px; margin: 0 auto; }
.head { display:flex; justify-content:space-between; align-items:center; margin-bottom: 12px; }
.title { margin:0; }
.card { background:#fff; border:1px solid #e5e7eb; border-radius:12px; padding: 14px; }

.row { display:flex; flex-direction:column; gap:6px; margin-bottom: 12px; }
.row2 { display:grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 720px){ .row2{ grid-template-columns: 1fr; } }

.rowInline{ display:flex; gap:10px; align-items:center; }
.hint { font-size: 12px; color:#6b7280; margin-top: 2px; }

label { font-size: 12px; font-weight: 800; color:#374151; }
.area { min-height: 140px; height: auto; resize: vertical; }
.actions { display:flex; gap:10px; justify-content:flex-end; margin-top: 6px; }

.warn { padding:10px 12px; border-radius:10px; border:1px solid #fde68a; background:#fffbeb; color:#92400e; margin-bottom: 10px; }
.err { padding:10px 12px; border-radius:10px; border:1px solid #fecaca; background:#fff5f5; color:#b91c1c; margin-bottom: 10px; }

.btn { background:#fff; border:1px solid #fde2e2; color:#e00000; border-radius:10px; padding:9px 14px; font-weight:800; cursor:pointer; text-decoration:none; display:inline-flex; align-items:center; }
.btn.secondary { border-color:#e5e7eb; color:#111827; }
.btn.small{ padding:6px 10px; font-size:12px; border-radius:9px; }
.btn:disabled { opacity:0.6; cursor:not-allowed; }
</style>
